import { THEME } from '../ui/editorTheme';

/**
 * The document reading measure and type scale (documents redesign card 2.2) —
 * ONE place for the numbers, used three ways:
 *
 *  1. `DOC_THEME`: the Lexical theme the documents editor mounts with. It is
 *     the shared THEME plus a `doc-*` class per block, so the scale applies
 *     through the theme (docTypography.css styles those classes) and never
 *     through per-node inline styles. Other editor hosts (task cards,
 *     collection docs) keep the plain THEME and their compact look.
 *  2. `DOC_MEASURE`: the reading column the editor body and the title block
 *     share.
 *  3. `docExportCss()`: the same scale as tag selectors for the HTML export.
 *     Saved bodies carry no classes (the sanitizer strips them), so an export
 *     can only style by tag — from these tokens, so the two cannot drift.
 *
 * Dark is the design of record ("Documents — reading view" artboard); light
 * keeps the same sizes with equivalent greys.
 */

export const DOC_MEASURE = Object.freeze({
  maxWidth: 760,
  padTop: 56,
  padSide: 48,
  padSideNarrow: 24,
  padBottom: 96,
  /** Below this window width the side padding drops to `padSideNarrow` (card 2.6). */
  narrowBelow: 640,
});

export const DOC_TYPE = Object.freeze({
  fontMono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  h1: { size: 40, line: 1.15, weight: 700, tracking: '-0.02em', margin: '0 0 10px' },
  h2: { size: 24, line: 1.25, weight: 650, tracking: '-0.01em', margin: '36px 0 12px' },
  h3: { size: 18, line: 1.3, weight: 600, tracking: '0', margin: '28px 0 8px' },
  body: { size: 15.5, line: 1.65, margin: '0 0 14px' },
  code: { size: 12.5, radius: 5 },
  scrollMarginTop: 80,
  dark: {
    heading: '#f4f4f5',
    title: '#fafafa',
    body: '#c8c8cf',
    marker: '#86868f', // 4.5:1 on #0e0e11 (card 6.3)
    strong: '#f4f4f5',
    em: '#d4d4d8',
    codeBg: '#1a1a20',
    codeBorder: '#27272e',
    codeText: '#e4e4e7',
    calloutBg: '#15151a',
    calloutBorder: '#24242b',
  },
  light: {
    heading: '#18181b',
    title: '#09090b',
    body: '#3f3f46',
    marker: '#6b6b74',
    strong: '#18181b',
    em: '#27272a',
    codeBg: '#f4f4f5',
    codeBorder: '#e4e4e7',
    codeText: '#27272a',
    calloutBg: '#fafafa',
    calloutBorder: '#e4e4e7',
  },
});

/** Callout kinds (card 2.5): accent / amber / green, each with its own icon. */
export const CALLOUT_COLORS = Object.freeze({
  info: '#b4a8ff',
  warning: '#fbbf24',
  success: '#4ade80',
});

const add = (base, extra) => `${base} ${extra}`;

/**
 * The documents editor's Lexical theme: THEME + the doc-* type classes. NOT
 * frozen: Lexical caches parsed class names on the theme object itself.
 */
export const DOC_THEME = {
  ...THEME,
  paragraph: add(THEME.paragraph, 'doc-p'),
  quote: add(THEME.quote, 'doc-quote'),
  heading: {
    h1: add(THEME.heading.h1, 'doc-h1'),
    h2: add(THEME.heading.h2, 'doc-h2'),
    h3: add(THEME.heading.h3, 'doc-h3'),
  },
  list: {
    ...THEME.list,
    ul: add(THEME.list.ul, 'doc-ul'),
    ol: add(THEME.list.ol, 'doc-ol'),
    listitem: add(THEME.list.listitem, 'doc-li'),
  },
  text: {
    ...THEME.text,
    bold: add(THEME.text.bold, 'doc-strong'),
    italic: add(THEME.text.italic, 'doc-em'),
    code: add(THEME.text.code, 'doc-code-inline'),
  },
  callout: add(THEME.callout || 'lx-callout', 'doc-callout'),
};

/**
 * The type scale as tag selectors, for a standalone exported HTML file (light
 * palette — an exported page is read and printed on white).
 */
export function docExportCss() {
  const c = DOC_TYPE.light;
  const h = (tag, t, color) =>
    `${tag} { font-size: ${t.size}px; line-height: ${t.line}; font-weight: ${t.weight}; letter-spacing: ${t.tracking}; margin: ${t.margin}; color: ${color}; }`;
  return [
    `body { font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: ${DOC_TYPE.body.size}px; line-height: ${DOC_TYPE.body.line}; color: ${c.body}; max-width: ${DOC_MEASURE.maxWidth}px; margin: 0 auto; padding: ${DOC_MEASURE.padTop}px ${DOC_MEASURE.padSideNarrow}px ${DOC_MEASURE.padBottom}px; }`,
    h('h1', DOC_TYPE.h1, c.title),
    h('h2', DOC_TYPE.h2, c.heading),
    h('h3', DOC_TYPE.h3, c.heading),
    `p, ul, ol { margin: ${DOC_TYPE.body.margin}; }`,
    'ul, ol { padding-left: 26px; } li { margin-bottom: 6px; }',
    `li::marker { color: ${c.marker}; }`,
    `strong, b { color: ${c.strong}; font-weight: 600; } em, i { color: ${c.em}; }`,
    `code { font-family: ${DOC_TYPE.fontMono}; font-size: ${DOC_TYPE.code.size}px; background: ${c.codeBg}; border: 1px solid ${c.codeBorder}; color: ${c.codeText}; padding: 1px 6px; border-radius: ${DOC_TYPE.code.radius}px; }`,
    `pre { background: ${c.codeBg}; border: 1px solid ${c.codeBorder}; border-radius: 8px; padding: 10px 12px; overflow: auto; }`,
    'pre code { background: transparent; border: 0; padding: 0; }',
    `blockquote { margin: 0 0 14px; padding-left: 14px; border-left: 3px solid ${c.codeBorder}; color: ${c.body}; }`,
    `div[data-callout] { margin: 0 0 18px; padding: 14px 16px 14px 44px; border-radius: 10px; background: ${c.calloutBg}; border: 1px solid ${c.calloutBorder}; position: relative; }`,
    ...Object.entries(CALLOUT_COLORS).map(
      ([kind, color]) =>
        `div[data-callout="${kind}"]::before { content: "${{ info: 'i', warning: '!', success: '✓' }[kind]}"; position: absolute; left: 16px; top: 14px; width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid ${color}; color: ${color}; font-size: 11px; font-weight: 700; line-height: 16px; text-align: center; }`
    ),
    'a { color: #6d5dd3; } img { max-width: 100%; }',
    'table { border-collapse: collapse; margin: 0 0 14px; } th, td { border: 1px solid #e4e4e7; padding: 6px 10px; }',
  ].join('\n  ');
}
